import { ArrowForward } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  keyframes,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { BRAND_PRIMARY, RADIUS_SM } from "../../theme/theme";
import { ArgusEye } from "./argus-eye";

/**
 * Branded Argus onboarding hero — screen 1 of the Pencil mockup.
 *
 * Two phases:
 *   - `booting`: pulsing "Spinning up your Argus workspace…" status pill,
 *     CTA disabled. `elapsedSeconds` drives the live "~Xs" timer when
 *     passed; we fall back to "~45s" when the caller doesn't track it.
 *   - `ready`: status pill flips to the active state and the CTA is
 *     enabled. The parent component owns the transition to the
 *     embedded Argus workspace.
 *
 * Design rule (see `new-fe/src/theme/theme.ts` + root CLAUDE.md): the
 * pulsing pill animates only `transform` + `opacity` — no layout
 * properties — to keep paint cheap while the page is mostly idle.
 */

// CSS keyframes — opacity-only fade so the button breathes without
// changing size (scale would nudge the layout around it during booting).
const pulse = keyframes`
  0%   { opacity: 0.7; }
  50%  { opacity: 1; }
  100% { opacity: 0.7; }
`;

/** Pretty-format "Xs" / "Ym Zs" from a seconds count. */
const formatElapsed = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};

const STEPS = [
  "Tell Argus about your app",
  "Create your first tests — Argus drives a real browser",
  "Schedule smokes that ping you when something breaks",
  "Wire up argus-action on your repo",
] as const;

export interface ArgusOnboardingHeroProps {
  phase: "booting" | "ready" | "error";
  onStart: () => void;
  /**
   * Optional live timer (seconds since the hero mounted). When omitted,
   * the status pill falls back to the static "~45s" placeholder.
   */
  elapsedSeconds?: number;
  /**
   * Error-phase only: disable the Retry button while a retry is in flight.
   */
  isRetrying?: boolean;
}

export const ArgusOnboardingHero = ({
  phase,
  onStart,
  elapsedSeconds,
  isRetrying = false,
}: ArgusOnboardingHeroProps) => {
  const isReady = phase === "ready";
  const isError = phase === "error";
  const isBooting = phase === "booting";
  const bootingTooltip =
    typeof elapsedSeconds === "number"
      ? `Argus is provisioning your sandbox (≈45s). Elapsed: ${formatElapsed(elapsedSeconds)}.`
      : "Argus is provisioning your sandbox (≈45s).";

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems="center"
      justifyContent="center"
      spacing={{ xs: 4, md: 8 }}
      sx={{
        flex: 1,
        width: "100%",
        maxWidth: 1280,
        mx: "auto",
        px: { xs: 3, md: 6 },
        py: { xs: 4, md: 8 },
      }}
    >
      {/* Left column ------------------------------------------------ */}
      <Stack direction="column" spacing={3} sx={{ flex: 1, maxWidth: 620 }}>
        {/* BETA chip (replaces the former "NEW · Argus" pill) */}
        <Box
          sx={{
            alignSelf: "flex-start",
            px: 1.5,
            py: 0.5,
            borderRadius: RADIUS_SM,
            bgcolor: "rgba(22, 0, 234, 0.08)",
            border: `1px solid rgba(22, 0, 234, 0.16)`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: BRAND_PRIMARY,
              fontWeight: 700,
              letterSpacing: "0.16em",
              lineHeight: 1.4,
            }}
          >
            BETA
          </Typography>
        </Box>

        {/* Headline — black per design */}
        <Typography
          component="h1"
          sx={{
            fontFamily: "'Geist', sans-serif",
            fontSize: { xs: "3rem", md: "4.5rem" },
            fontWeight: 600,
            color: "text.primary",
            letterSpacing: "-0.02em",
            lineHeight: 1.02,
          }}
        >
          Meet Argus.
        </Typography>

        {/* Subtitle — larger than h6 */}
        <Typography
          sx={{
            color: "text.secondary",
            fontWeight: 400,
            maxWidth: 580,
            lineHeight: 1.4,
            fontSize: { xs: "1.25rem", md: "1.5rem" },
          }}
        >
          An agent that tests your product — so you don't have to guess.
        </Typography>

        {/* What happens next — preview list. Not a progress tracker; the
            real checklist is TODOS.md inside the workspace, which the
            agent ticks off live. First bullet is filled + primary colored
            to hint at "we start here"; the rest are empty light-gray
            circles to read as "coming up". */}
        <Stack component="ol" spacing={1.25} sx={{ p: 0, m: 0, pt: 1, listStyle: "none" }}>
          {STEPS.map((step, index) => {
            const active = index === 0;
            return (
              <Stack key={step} component="li" direction="row" alignItems="center" spacing={1.75}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: active ? BRAND_PRIMARY : "transparent",
                    border: active ? `2px solid ${BRAND_PRIMARY}` : "2px solid rgba(0, 0, 0, 0.15)",
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: active ? "text.primary" : "text.secondary",
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  {step}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        {/* Unified CTA — same layout across booting/ready/error. Booting
            shows a spinner + live timer; ready flips to "Start"; error
            flips to a red caption + Retry + a fallback link to the
            manual onboarding. */}
        <Stack direction="column" alignItems="flex-start" spacing={1.25} sx={{ pt: 1 }}>
          {isError && (
            <Typography variant="body2" sx={{ color: "error.main", fontWeight: 500 }}>
              Argus couldn't boot. Retry in a moment — if it keeps failing,{" "}
              <Link href="mailto:contact@desplega.ai" sx={{ color: "error.main", fontWeight: 600 }}>
                ping the team
              </Link>
              .
            </Typography>
          )}
          <Tooltip title={isBooting ? bootingTooltip : ""} arrow placement="right">
            {/* Wrap disabled button in a span so the tooltip still hears
                hover events — MUI requirement. */}
            <Box component="span" sx={{ display: "inline-block" }}>
              <Button
                variant="contained"
                size="large"
                onClick={onStart}
                disabled={isBooting || (isError && isRetrying)}
                startIcon={
                  isBooting || (isError && isRetrying) ? (
                    <CircularProgress
                      size={16}
                      thickness={5}
                      sx={{ color: "common.white", opacity: 0.9 }}
                    />
                  ) : undefined
                }
                endIcon={
                  isBooting || (isError && isRetrying) ? undefined : (
                    <ArrowForward fontSize="small" />
                  )
                }
                sx={{
                  bgcolor: isError ? "error.main" : BRAND_PRIMARY,
                  px: 3,
                  py: 1.25,
                  fontSize: "1rem",
                  fontWeight: 500,
                  minWidth: 260,
                  // Keep the color + size steady when disabled so the button
                  // reads as "actively working" instead of "unavailable".
                  // Only opacity breathes — no scale, no layout thrash.
                  "&.Mui-disabled": {
                    bgcolor: isError ? "error.main" : BRAND_PRIMARY,
                    color: "common.white",
                    opacity: 0.85,
                    animation: `${pulse} 2s ease-in-out infinite`,
                  },
                  "&:hover": {
                    bgcolor: isError ? "error.dark" : "primary.dark",
                  },
                }}
              >
                {isError
                  ? isRetrying
                    ? "Retrying…"
                    : "Retry"
                  : isReady
                    ? "Start"
                    : "Spinning up Argus…"}
              </Button>
            </Box>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Right column — branded eye illustration ------------------- */}
      <Box
        aria-hidden
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          width: { md: 400 },
          height: { md: 400 },
          flexShrink: 0,
        }}
      >
        <ArgusEye />
      </Box>
    </Stack>
  );
};
